import type { OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { Datastore } from '@freelancer/datastore';
import type {
  TaskGroup,
  TaskGroupCollection,
} from '@freelancer/datastore/collections';
import { trackById } from '@freelancer/ui/helpers';
import {
  IconSize,
  flPlaylistAddCheck,
  provideFlIcons,
} from '@freelancer/ui/icon';
import { TabsDirection, TabsSize } from '@freelancer/ui/tabs';
import { TaskGroupStatusApi } from 'api-typings/tasklist/tasklist';
import type { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

/**
 * This component lists all task-lists.
 */
@Component({
  providers: [provideFlIcons({ flPlaylistAddCheck })],
  standalone: false,
  selector: 'fl-tasklists',
  template: `
    <fl-scrollable-content class="TasklistsScrollableContent">
      <fl-tabs
        [direction]="TabsDirection.COLUMN_RIGHT"
        [truncate]="true"
        [size]="TabsSize.XSMALL"
      >
        @if (tasklistsToShow$ | flAsync; as taskGroups) {
          <ng-container ngProjectAs="fl-tab-item">
            @for (taskGroup of taskGroups; track trackById($index, taskGroup)) {
              <fl-tab-item
                flTrackingLabel="TaskGroupsCollectionClick"
                flTrackingSection="taskGroupLists"
                [flTrackingReferenceType]="'task_group_id'"
                [flTrackingReferenceId]="taskGroup.id"
                [iconName]="'flPlaylistAddCheck'"
                [iconSize]="IconSize.SMALL"
                [link]="'/tasklists/' + taskGroup.id"
                [preserveBackButton]="false"
                [titleText]="taskGroup.name"
              >
              </fl-tab-item>
            }
          </ng-container>
        }
      </fl-tabs>
    </fl-scrollable-content>
  `,
  styleUrls: ['./tasklists.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TasklistsComponent implements OnInit {
  private datastore = inject(Datastore);

  IconSize = IconSize;
  TabsDirection = TabsDirection;
  TabsSize = TabsSize;
  trackById = trackById;

  @Input() customLimit = 3;

  tasklistsToShow$: Observable<readonly TaskGroup[]>;

  ngOnInit(): void {
    const taskGroupCollection = this.datastore.collection<TaskGroupCollection>(
      'taskGroup',
      query => query.where('status', '==', TaskGroupStatusApi.OPEN),
    );

    this.tasklistsToShow$ = taskGroupCollection
      .valueChanges()
      .pipe(map(taskGroups => taskGroups.slice(0, this.customLimit)));
  }
}
